import { Button, Checkbox, FormControlLabel, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { CodeResponse, useGoogleLogin } from '@react-oauth/google';
import { Form, Formik } from 'formik';
import Typography from '@mui/material/Typography';
import * as yup from 'yup';
import { useAuthInfo } from '@propelauth/react';
import RotatingLoader from '../common/ui/RotatingLoader';
import {
  PropelUserModel,
  UserPreferencesOut,
  UserPreferencesService,
  UserPreferencesUpdateDTO,
  UserService,
} from '../api';

type PermanentUserAvailabilityProps = {
  userPreferences?: UserPreferencesOut;
  isLoadingUserPreferences: boolean;
  refetchUserPrefs: () => Promise<any>;
  selectedUserId?: string;
};
export const UserSyncSettings = ({
  userPreferences,
  isLoadingUserPreferences,
  refetchUserPrefs,
  selectedUserId,
}: PermanentUserAvailabilityProps) => {
  const { user } = useAuthInfo();
  const [isRefetching, setIsRefetching] = useState(false);
  const [users, setUsers] = useState<PropelUserModel[] | undefined>();
  const { t } = useTranslation();
  const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);
  useEffect(() => {
    if (selectedUserId === userPreferences?.propel_id) {
      return;
    }
    const refetchDataWithNewUser = async () => {
      setIsRefetching(true);
      await refetchUserPrefs();
      setIsRefetching(false);
    };

    void refetchDataWithNewUser();
  }, [selectedUserId]);
  useEffect(() => {
    const fetchUsers = async () => {
      const users = await UserService.getUsers();
      if (setUsers) {
        setUsers(users);
      }
    };
    void fetchUsers();
  }, []);
  const initialValues: UserPreferencesUpdateDTO = {
    should_send_shift_notifications: userPreferences?.should_send_shift_notifications || false,
    should_send_workshop_assignment_notifications:
      userPreferences?.should_send_workshop_assignment_notifications || false,
    google_integration_enabled: userPreferences?.google_integration_enabled || false,
    qualified_order_pages_settings: userPreferences?.qualified_order_pages_settings || [],
  };
  const validationSchema = yup.object().shape({
    should_send_shift_notifications: yup.boolean().required(),
    should_send_workshop_assignment_notifications: yup.boolean().required(),
    google_integration_enabled: yup.boolean(),
  });

  const onSubmit = async (values: UserPreferencesUpdateDTO) => {
    setIsUpdatingSettings(true);
    await UserPreferencesService.editUserPreferences(values);
    await refetchUserPrefs();
    setIsUpdatingSettings(false);
  };

  const onLoginSuccess = async (codeResponse: Omit<CodeResponse, 'error' | 'error_description' | 'error_uri'>) => {
    setIsUpdatingSettings(true);
    await UserPreferencesService.gcalConnect({
      auth_code: codeResponse.code,
    });

    await refetchUserPrefs();
    setIsUpdatingSettings(false);
  };
  const disconnectFromGoogle = async () => {
    setIsUpdatingSettings(true);
    const newSettings: UserPreferencesUpdateDTO = {
      should_send_shift_notifications: userPreferences?.should_send_shift_notifications || false,
      should_send_workshop_assignment_notifications:
        userPreferences?.should_send_workshop_assignment_notifications || false,
      qualified_order_pages_settings: userPreferences?.qualified_order_pages_settings || [],
      google_integration_enabled: false,
    };

    await UserPreferencesService.editUserPreferences(newSettings);

    await refetchUserPrefs();
    setIsUpdatingSettings(false);
  };

  const login = useGoogleLogin({
    onSuccess: onLoginSuccess,
    scope: 'https://www.googleapis.com/auth/calendar',
    flow: 'auth-code',
    include_granted_scopes: false,
  });
  if (isLoadingUserPreferences || isUpdatingSettings || isRefetching) {
    return (
      <Grid
        container
        sx={{
          minHeight: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <RotatingLoader />
      </Grid>
    );
  }

  return (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ errors, touched, values, handleSubmit, handleBlur, handleChange }) => (
        <Form onSubmit={handleSubmit}>
          <Grid container flexDirection="column">
            <Grid item mb={2}>
              <Typography variant="h5" fontWeight={700}>
                {t('userSettings.sync')}
              </Typography>
            </Grid>
            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox
                    name="should_send_shift_notifications"
                    onBlur={handleBlur}
                    checked={values.should_send_shift_notifications}
                    onChange={handleChange}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
                label={t('userSettings.sendShiftNotifications')}
              />
              {touched.should_send_shift_notifications && errors.should_send_shift_notifications && (
                <Typography color="error">{errors.should_send_shift_notifications}</Typography>
              )}
            </Grid>
            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox
                    name="should_send_workshop_assignment_notifications"
                    onBlur={handleBlur}
                    checked={values.should_send_workshop_assignment_notifications}
                    onChange={handleChange}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
                label={t('userSettings.sendWorkshopNotifications')}
              />
              {touched.should_send_workshop_assignment_notifications &&
                errors.should_send_workshop_assignment_notifications && (
                  <Typography color="error">{errors.should_send_workshop_assignment_notifications}</Typography>
                )}
            </Grid>
            <Grid container flexDirection="column" gap={2} mt={4}>
              <Typography fontWeight={700}>{t('userSettings.whereToSync')}</Typography>

              {userPreferences?.google_integration_enabled ? (
                <Grid container alignItems="center" gap={2}>
                  <img src={'/images/gcallogo.png'} width="24px" />
                  <Typography>
                    {t('userSettings.gcalSyncEmail', {
                      userEmail: userPreferences.gcal_synced_email,
                    })}
                  </Typography>
                  <Grid item mx={4}>
                    <Button variant="outlined" onClick={disconnectFromGoogle}>
                      {t('userSettings.disconnect')}
                    </Button>
                  </Grid>
                </Grid>
              ) : (
                <Grid container flexDirection="column" gap={2}>
                  <Typography>
                    {t('userSettings.userEmailSync', {
                      userEmail: users?.find((x) => x.user_id === selectedUserId)?.email || user?.email,
                    })}
                  </Typography>
                  <Typography>{t('userSettings.googleSync')}:</Typography>
                  <Grid item>
                    <Button
                      onClick={login}
                      variant="outlined"
                      color="inherit"
                      dir="ltr"
                      startIcon={<img src={'/images/gcallogo.png'} style={{ width: '24px' }} />}
                      sx={{ textTransform: 'none' }}>
                      Connect Google Calendar
                    </Button>
                  </Grid>
                </Grid>
              )}
            </Grid>
            <Grid container justifyContent="flex-end" mt={2}>
              <Button
                type="submit"
                variant="contained"
                disabled={isUpdatingSettings || user?.userId !== selectedUserId}>
                {t('save')}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
